import { FullscreenFormPreviewModal } from '@/components/modal/fullscreen-form-preview-modal';
import { RegulatoryGuidelineWarningModal } from '@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-warning-modal';
import { AddToLibraryButton } from '@/container/community/addToLibraryButton';
import { communityTemplateDetailsMessages } from '@/container/community/communityTemplateDetails.messages';
import { LikeButton } from '@/container/community/like-button';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { PageShell } from '@heidi/design-system/page-shell';
import { Skeleton } from '@heidi/design-system/skeleton';
import {
  TypographyH4,
  TypographyP2,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { DetailsHeadMeta } from './components/DetailsHeadMeta';
import { DetailsHeader } from './components/DetailsHeader';
import { DetailsSidebar } from './components/DetailsSidebar';
import { FullscreenXmlFormPreviewModal } from './components/FullscreenXmlFormPreviewModal';
import { HowToUseSection } from './components/HowToUseSection';
import { ShareActions } from './components/ShareActions';
import { TemplateFormPreviewSection } from './components/TemplateFormPreviewSection';
import { TemplatePreviewSection } from './components/TemplatePreviewSection';
import { TemplateTags } from './components/TemplateTags';
import { TemplateXmlFormPreviewSection } from './components/TemplateXmlFormPreviewSection';
import { useCommunityTemplateDetailsViewModel } from './hooks/useCommunityTemplateDetailsViewModel';

const CommunityTemplateDetails = () => {
  const {
    activePdfUrl,
    dateFormat,
    getUrlWithoutHash,
    handleBackClick,
    handleBadgeClick,
    handleCopyLinkClick,
    handleDownloadPdf,
    handleDownloadXml,
    handleOpenFullscreenPreview,
    handleFormDemoSubmit,
    handleFormDemoTrySampleClick,
    handlePostFillSignupClick,
    handleSaveTemplateAnyway,
    hasS3ObjectKey,
    howToUseRichValues,
    isFormTemplate,
    isXmlFormTemplate,
    setXmlFillResult,
    xmlFieldValues,
    xmlSource,
    jsonSource,
    jsonFieldValues,
    setJsonFieldValues,
    isFullscreenPreviewOpen,
    isLikesEnabled,
    isLoading,
    metaDescription,
    metaKeywords,
    outerShellClassName,
    pageTitle,
    setDemoFilledPdfUrl,
    setIsFullscreenPreviewOpen,
    template,
    templateAlreadyExists,
    isAuthenticated,
  } = useCommunityTemplateDetailsViewModel();

  const innerShellClassName = cn(
    'flex flex-col items-center gap-10 py-8 sm:gap-16',
    !isAuthenticated && 'h-fit min-h-full'
  );
  const formPreviewPdfUrl =
    isFormTemplate && hasS3ObjectKey && activePdfUrl ? activePdfUrl : null;
  const formPreviewXmlSource =
    isXmlFormTemplate && xmlSource && !formPreviewPdfUrl ? xmlSource : null;
  const formPreviewJsonSource =
    jsonSource && !formPreviewPdfUrl && !formPreviewXmlSource ? jsonSource : null;
  const structuredFieldValues = formPreviewXmlSource ? xmlFieldValues : jsonFieldValues;
  const formPreviewSectionRef = useRef<HTMLDivElement>(null);

  const handleTryThisFormClick = useCallback(() => {
    setIsFullscreenPreviewOpen(false);
    formPreviewSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [setIsFullscreenPreviewOpen]);

  return (
    <>
      <DetailsHeadMeta
        pageTitle={pageTitle}
        metaDescription={metaDescription}
        metaKeywords={metaKeywords}
        template={template}
      />

      <div className={outerShellClassName}>
        <PageShell.Container className={innerShellClassName}>
          <div className="flex w-full">
            <Button
              variant="ghost"
              size="sm"
              flush="start"
              className="flex w-fit"
              onClick={handleBackClick}
            >
              <ArrowLeft className="rtl:-scale-x-100" />
              <FormattedMessage
                {...communityTemplateDetailsMessages.backToTemplateCommunity}
              />
            </Button>
          </div>

          <div className="flex w-full gap-8">
            <div className="flex w-full flex-col gap-6 lg:gap-12">
              <DetailsHeader
                template={template}
                isLoading={isLoading}
                dateFormat={dateFormat}
              />

              <div className="flex flex-col gap-4 lg:hidden">
                <AddToLibraryButton
                  templateId={template?.template_id}
                  templateAlreadyExists={templateAlreadyExists}
                />

                <div className="flex flex-col gap-2">
                  <TypographyPBold>
                    <FormattedMessage {...communityTemplateDetailsMessages.tagsHeading} />
                  </TypographyPBold>
                  <TemplateTags
                    template={template}
                    onBadgeClick={handleBadgeClick}
                    emphasizeText
                  />
                </div>
              </div>

              {isLikesEnabled && template?.template_id && (
                <div className="flex items-center">
                  <LikeButton
                    templateId={template.template_id}
                    likesCount={(template as { likes_count?: number }).likes_count ?? 0}
                    size="md"
                  />
                </div>
              )}

              <div className="flex flex-col gap-3">
                <TypographyH4>
                  <FormattedMessage
                    {...communityTemplateDetailsMessages.aboutThisTemplate}
                  />
                </TypographyH4>
                {isLoading ? (
                  <>
                    <Skeleton className="h-4 w-full max-w-md" />
                    <Skeleton className="h-4 w-full max-w-lg" />
                    <Skeleton className="h-4 w-full max-w-lg" />
                  </>
                ) : (
                  <TypographyP2>
                    {template?.description || (
                      <FormattedMessage
                        {...communityTemplateDetailsMessages.generatingDescription}
                      />
                    )}
                  </TypographyP2>
                )}
              </div>

              <div ref={formPreviewSectionRef} className="flex flex-col gap-3 @container">
                <TypographyH4>
                  <FormattedMessage
                    {...communityTemplateDetailsMessages.previewTemplate}
                  />
                </TypographyH4>
                <div className="flex w-full flex-col gap-4 rounded-lg border bg-surface p-4">
                  {formPreviewPdfUrl ? (
                    <TemplateFormPreviewSection
                      activePdfUrl={formPreviewPdfUrl}
                      isLoading={isLoading}
                      onOpenFullscreenPreview={handleOpenFullscreenPreview}
                      onFormDemoSubmit={handleFormDemoSubmit}
                      onFormDemoTrySampleClick={handleFormDemoTrySampleClick}
                      onDownloadPdf={() => {
                        void handleDownloadPdf();
                      }}
                      onFilledPdfReady={(url) => setDemoFilledPdfUrl(url)}
                      onResetDemoPdf={() => setDemoFilledPdfUrl(null)}
                      onSignupCtaClick={handlePostFillSignupClick}
                      template={template}
                      isAuthenticated={isAuthenticated}
                    />
                  ) : formPreviewXmlSource || formPreviewJsonSource ? (
                    <TemplateXmlFormPreviewSection
                      xmlSource={formPreviewXmlSource}
                      fieldValues={structuredFieldValues}
                      isLoading={isLoading}
                      onDownloadXml={formPreviewXmlSource ? handleDownloadXml : undefined}
                      onOpenFullscreenPreview={handleOpenFullscreenPreview}
                      onXmlFilled={setXmlFillResult}
                      onJsonFilled={setJsonFieldValues}
                      onResetXmlFill={() => {
                        setXmlFillResult(null);
                        setJsonFieldValues(null);
                      }}
                      onFormDemoSubmit={handleFormDemoSubmit}
                      onFormDemoTrySampleClick={handleFormDemoTrySampleClick}
                      onSignupCtaClick={handlePostFillSignupClick}
                      template={template}
                      isAuthenticated={isAuthenticated}
                    />
                  ) : (
                    <TemplatePreviewSection isLoading={isLoading} template={template} />
                  )}
                </div>
              </div>

              <AddToLibraryButton
                templateId={template?.template_id}
                templateAlreadyExists={templateAlreadyExists}
              />

              <HowToUseSection
                howToUseRichValues={howToUseRichValues}
                isFormTemplate={isFormTemplate}
              />

              <div className="flex flex-col gap-2 lg:hidden">
                <TypographyPBold>
                  <FormattedMessage {...communityTemplateDetailsMessages.shareHeading} />
                </TypographyPBold>

                <ShareActions
                  getUrlWithoutHash={getUrlWithoutHash}
                  onCopyLinkClick={handleCopyLinkClick}
                />
              </div>
            </div>

            <DetailsSidebar
              getUrlWithoutHash={getUrlWithoutHash}
              onBadgeClick={handleBadgeClick}
              onCopyLinkClick={handleCopyLinkClick}
              template={template}
              templateAlreadyExists={templateAlreadyExists}
            />
          </div>
        </PageShell.Container>
      </div>

      <RegulatoryGuidelineWarningModal onSaveAnyway={handleSaveTemplateAnyway} />

      {activePdfUrl && (
        <FullscreenFormPreviewModal
          isOpen={isFullscreenPreviewOpen}
          onOpenChange={setIsFullscreenPreviewOpen}
          pdfUrl={activePdfUrl}
          templateTitle={template?.title}
          onDownload={() => {
            void handleDownloadPdf();
          }}
          headerActions={
            formPreviewPdfUrl ? (
              <Button variant="default" size="md" onClick={handleTryThisFormClick}>
                <FormattedMessage
                  {...communityTemplateDetailsMessages.tryFillFormButton}
                />
              </Button>
            ) : undefined
          }
        />
      )}

      {(formPreviewXmlSource || formPreviewJsonSource) && (
        <FullscreenXmlFormPreviewModal
          isOpen={isFullscreenPreviewOpen}
          onOpenChange={setIsFullscreenPreviewOpen}
          xmlSource={formPreviewXmlSource}
          formFields={template?.template_content?.form_fields ?? []}
          fieldValues={structuredFieldValues}
          onDownload={formPreviewXmlSource ? handleDownloadXml : undefined}
          headerActions={
            <Button variant="default" size="md" onClick={handleTryThisFormClick}>
              <FormattedMessage {...communityTemplateDetailsMessages.tryFillFormButton} />
            </Button>
          }
        />
      )}
    </>
  );
};

export default CommunityTemplateDetails;
